import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';
import { AppConfig } from 'types';
import { buildAppState, Reducer } from 'store/state';
import { buildHttp } from 'services/http';
import { loadState, Translate } from 'helpers';
import { PageKeys } from 'consts';
import { translationsAction } from 'store/actions';

export const ApplicationContext = createContext({} as ReturnType<typeof ApplicationProvider>);

const INITIAL_STATE = loadState(buildAppState());

export function ApplicationProvider(config: AppConfig) {
	const [state, dispatch] = useReducer(Reducer, INITIAL_STATE);
	const { data, user } = state;
	const { language, translations } = state.language;
	const http = useMemo(() => buildHttp(user.token, dispatch), [user.token, dispatch]);

	const translate = useCallback(
		(key = '', page: PageKeys = 'general', exact = false) => {
			return Translate(key, page, exact, language, translations);
		},
		[language, translations]
	);

	const dataSelector = useCallback(
		(url: string, defaults?: any) => (undefined === data[url] ? defaults : data[url]),
		[data]
	);

	useEffect(() => {
		const url = config.API_CONTENT_URL || config.API.external.contentApi;
		const promises = Object.keys(config.API.content).map(page => {
			const listguid = config.API.content[page as PageKeys];
			const payload = { listguid, language: language.toLowerCase() };
			return http.post(url, payload, undefined, {});
		});
		Promise.all(promises).then(results => {
			const translations = results.reduce((map, item) => {
				const { language, ...pageTranslations } = item;
				return { ...map, ...pageTranslations };
			}, {});
			dispatch([
				translationsAction,
				{
					language: {
						...state.language,
						translations
					}
				}
			]);
		});
	}, [language]); // eslint-disable-line

	return {
		config,
		dataSelector,
		dispatch,
		http,
		state,
		t: translate,
		translate
	};
}
